import React from 'react';
import styled from '@emotion/styled/macro';
import vars from '@variables';
import CallToActionLinkAtom from '@atoms/link-atoms/call-to-action-link-atom';
import { H2Atom, PAtom } from '@atoms/text-atoms/text-atoms';
import { defineResponsiveGridColumns } from '@utils/utils';
import { CallToActionBlockProps } from './call-to-action.types';

export const ButtonWrapper = styled.div`
  ${CallToActionLinkAtom} {
    margin-right: ${vars.spacingM};
    margin-bottom: ${vars.spacingM};

    &:last-of-type {
      margin-right: 0;
      margin-bottom: 0;
    }
  }
`;

const CallToActionBlock: React.FC<CallToActionBlockProps> = ({
  title,
  anchorId,
  description,
  buttonText,
  internalUrl,
  externalUrl,
  colour = vars.coreDarkGrey,
  className,
  secondButtonText,
  secondInternalUrl,
  secondExternalUrl,
}) => {
  if (buttonText && (internalUrl || externalUrl)) {
    return (
      <div
        id={anchorId}
        className={className}
        data-testid="call-to-action-block"
      >
        <div className="component-grid-structure">
          <div className="call-to-action-block-content">
            {title && (
              <H2Atom data-testid="call-to-action-block-title">{title}</H2Atom>
            )}
            {description && (
              <PAtom data-testid="call-to-action-block-description">
                {description}
              </PAtom>
            )}

            <ButtonWrapper>
              <CallToActionLinkAtom
                buttonText={buttonText}
                url={internalUrl || externalUrl}
                isExternal={!internalUrl && externalUrl ? true : false}
                highlightColour={colour}
              />
              {secondButtonText && (secondExternalUrl || secondInternalUrl) && (
                <CallToActionLinkAtom
                  buttonText={secondButtonText}
                  url={secondInternalUrl || secondExternalUrl}
                  isExternal={
                    !secondInternalUrl && secondExternalUrl ? true : false
                  }
                  highlightColour={colour}
                />
              )}
            </ButtonWrapper>
          </div>
        </div>
      </div>
    );
  } else {
    return null;
  }
};

const StyledCallToActionBlock = styled(CallToActionBlock)`
  margin: ${({ anchorId }) => (anchorId ? vars.spacingL : '2rem')} auto
    ${vars.spacingL} auto;
  .component-grid-structure {
    display: grid;
    column-gap: ${vars.mobileColumnPercentageGap};
    grid-template-columns: ${defineResponsiveGridColumns(4)};

    @media screen and (min-width: ${vars.breakPointTablet}) {
      column-gap: ${vars.standardColumnPercentageGap};
      grid-template-columns: ${defineResponsiveGridColumns(8)};
    }

    @media screen and (min-width: ${vars.breakPointSmallDesktop}) {
      column-gap: ${vars.standardColumnPercentageGap};
      grid-template-columns: ${defineResponsiveGridColumns(12)};
    }

    .call-to-action-block-content {
      grid-column: col1-start/col4-end;

      @media screen and (min-width: ${vars.breakPointTablet}) {
        grid-column: col2-start/col7-end;
      }

      @media screen and (min-width: ${vars.breakPointSmallDesktop}) {
        grid-column: col3-start/col10-end;
      }
    }
  }

  ${H2Atom} {
    font-weight: ${vars.fontWeightBold};
    padding: 0;
    margin-bottom: ${({ description }) =>
      description ? vars.spacingXs : vars.spacingL};
  }
  ${PAtom} {
    margin-bottom: ${vars.spacingL};
  }
`;

export default StyledCallToActionBlock;
